import React from "react";
import { Box, Divider, IconButton, Tooltip } from "@material-ui/core";
import parseToolbar from "./parseToolbar";
import { BUILT_IN_ITEMS } from "./toolbarItems";
import TableContextStrip from "./TableContextStrip";
import { shortcutLabel } from "./shortcuts";

export default function Toolbar({ editor, toolbar, customItems = {} }) {
  // The editor instance only exists after the mount effect has run, so the
  // first render has nothing to drive button state from.
  if (!editor) return null;

  const groups = parseToolbar(toolbar);
  const registry = { ...BUILT_IN_ITEMS, ...customItems };

  // A contextual strip: only for toolbars that offer `table` in the first
  // place, and only while the caret is actually inside one. Every format that
  // carries a table - the order sheet's two, the issues sheet's parties block,
  // the exhibits list - is edited in place, so there is nothing to exempt.
  const showTableStrip =
    groups.some((group) => group.indexOf("table") !== -1) &&
    editor.isActive("table");

  return (
    <Box
      display="flex"
      alignItems="center"
      flexWrap="wrap"
      className="rte__toolbar"
    >
      {groups.map((group, groupIndex) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={groupIndex}>
          {groupIndex > 0 && (
            <Divider
              orientation="vertical"
              flexItem
              className="rte__toolbar-sep"
            />
          )}
          {group.map((name) => {
            const item = registry[name];
            // An unknown name is skipped rather than thrown on: toolbar
            // strings are copied verbatim from the TinyMCE configs, and a
            // stray plugin name should not take the editor down with it.
            if (!item) return null;
            if (item.render) {
              return (
                <React.Fragment key={name}>{item.render(editor)}</React.Fragment>
              );
            }
            const isActive = item.active ? item.active(editor) : false;
            const isDisabled = item.disabled ? item.disabled(editor) : false;
            // "Bold (Ctrl+B)" - looked up by toolbar name; see shortcuts.js.
            const shortcut = shortcutLabel(name);
            const title = item.title || name;
            return (
              <Tooltip
                key={name}
                title={shortcut ? `${title} (${shortcut})` : title}
              >
                {/* MUI's Tooltip cannot attach to a disabled child. */}
                <span>
                  <IconButton
                    size="small"
                    disabled={isDisabled}
                    color={isActive ? "primary" : "default"}
                    // Without this the mousedown blurs the editor, the
                    // selection collapses, and the command applies to nothing.
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => item.run(editor)}
                  >
                    {item.icon}
                  </IconButton>
                </span>
              </Tooltip>
            );
          })}
        </React.Fragment>
      ))}
      {/* Full-width, so the flex row wraps it onto a line of its own. */}
      {showTableStrip && <TableContextStrip editor={editor} />}
    </Box>
  );
}
